/**
 * The deposit review queue — the screen this console exists to provide.
 *
 * Two tabs: "Pending" (the original behavior — `submitted`/`under_review`
 * only, oldest first, since a queue should surface what's waited longest)
 * and "All" (every deposit ever, newest first, `Status` showing the real
 * outcome). Bug fixed 2026-08-24: a decided deposit used to vanish from
 * this screen entirely the moment it was approved or rejected — the only
 * list that had ever shown it. "All" is the fix; "Pending" stays the
 * default landing tab because it's still the actionable work list.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { etb, since, shortId } from '../format.ts';
import { href, navigate, type Route } from '../router.ts';
import { Empty, ErrorBanner, Spinner, Status } from '../components/ui.tsx';
import type { AdminDepositView } from '../api/types.ts';

const PAGE_SIZE = 50;

export function DepositQueue({ tab }: { tab?: Extract<Route, { name: 'deposits' }>['tab'] }) {
  const showAll = tab === 'all';
  const [deposits, setDeposits] = useState<AdminDepositView[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback((all: boolean, start: number) => {
    setDeposits(null);
    setError(null);
    if (!all) {
      void api
        .pendingDeposits()
        .then(({ deposits: next }) => {
          setDeposits(next);
          setTotal(next.length);
        })
        .catch(setError);
      return;
    }
    void api
      .deposits({ limit: PAGE_SIZE, offset: start })
      .then((res) => {
        setDeposits(res.deposits);
        setTotal(res.total);
      })
      .catch(setError);
  }, []);

  useEffect(() => {
    load(showAll, offset);
  }, [load, showAll, offset]);

  // Switching tabs resets paging — "page 3 of All" makes no sense on Pending.
  useEffect(() => {
    setOffset(0);
  }, [showAll]);

  return (
    <section>
      <header className="pagehead">
        <h1>{showAll ? 'Deposits' : 'Deposits awaiting review'}</h1>
        {deposits ? <span className="count">{total}</span> : null}
      </header>

      <div className="tabs">
        <button
          type="button"
          className={`tabs__tab${!showAll ? ' tabs__tab--on' : ''}`}
          onClick={() => navigate({ name: 'deposits' })}
        >
          Pending
        </button>
        <button
          type="button"
          className={`tabs__tab${showAll ? ' tabs__tab--on' : ''}`}
          onClick={() => navigate({ name: 'deposits', tab: 'all' })}
        >
          All
        </button>
      </div>

      <ErrorBanner error={error} />
      {deposits === null && !error ? <Spinner /> : null}
      {deposits?.length === 0 ? <Empty>{showAll ? 'No deposits yet.' : 'Nothing waiting.'}</Empty> : null}

      {deposits && deposits.length > 0 ? (
        <>
          <table className="table" role="table">
            <thead role="rowgroup">
              <tr role="row">
                <th scope="col" role="columnheader">Amount</th>
                <th scope="col" role="columnheader">Method</th>
                <th scope="col" role="columnheader">Reference</th>
                <th scope="col" role="columnheader">Player</th>
                <th scope="col" role="columnheader">Proof</th>
                <th scope="col" role="columnheader">{showAll ? 'Submitted' : 'Waiting'}</th>
                <th scope="col" role="columnheader">Status</th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {deposits.map((d) => (
                <tr role="row" key={d.id}>
                  <td role="cell" data-label="Amount">
                    <a className="link" href={href({ name: 'deposit', id: d.id })}>
                      {etb(d.amount)}
                    </a>
                    {/*
                      The claim and the Telebirr SMS disagreeing is the signal
                      that matters most on this row, and only the SMS figure is
                      ever credited. Surfaced in the queue for the same reason
                      as the reuse count below: a reviewer must see it before
                      forming an opinion, not after opening the deposit.
                      Silent when they agree — a badge on every ordinary row
                      teaches people to ignore the badge.
                    */}
                    {d.smsAmount !== null && d.smsAmount !== d.amount && (
                      <div className="mismatch" title="The Telebirr SMS confirms a different amount; only this is credited">
                        SMS: {etb(d.smsAmount)}
                      </div>
                    )}
                  </td>
                  <td role="cell" data-label="Method">{d.method}</td>
                  <td role="cell" data-label="Reference">
                    <code>{d.txReference}</code>
                  </td>
                  <td role="cell" data-label="Player">
                    <code className="muted">{shortId(d.userId)}</code>
                  </td>
                  <td role="cell" data-label="Proof">
                    {/*
                      The reuse count is surfaced in the *queue*, not only on the
                      detail screen. §7.2's whole purpose is that a reviewer
                      notices a re-used receipt; a signal they have to open a
                      deposit to see is one they will meet only after forming an
                      opinion about the claim.

                      No uploaded image is not "no evidence" — a Telebirr
                      claim's transaction reference (matched against the SMS
                      forwarder) is real evidence too, and "none" read as if
                      there were nothing to check the claim against at all.
                    */}
                    {d.proofId === null ? (
                      <code className="muted">{d.txReference}</code>
                    ) : d.proofReuseCount > 0 ? (
                      <span className="flag">reused ×{d.proofReuseCount}</span>
                    ) : (
                      <span>attached</span>
                    )}
                  </td>
                  <td role="cell" data-label={showAll ? 'Submitted' : 'Waiting'}>{since(d.createdAt)}</td>
                  <td role="cell" data-label="Status">
                    <Status value={d.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {showAll && total > PAGE_SIZE ? (
            <div className="actions">
              <button
                type="button"
                className="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                ‹ Newer
              </button>
              <span className="muted">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <button
                type="button"
                className="button"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Older ›
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
